// src/components/sections/EtTrinityOverview.jsx

import { motion } from "framer-motion";
import { Compass, Users, Rocket } from "lucide-react";

// First Created – 10 Nov 2025
// Author – Atul Pandey
// Purpose – Overview section for Trinity Platform page
// Updated – 11 Nov 2025 – Added icon glows, mobile polish, immersive styling
// Updated – 23 Nov 2025 – Copy refinements

export default function EtTrinityOverview() {
  return (
    <section id="overview" className="relative py-24 bg-black text-white overflow-hidden font-poppins">
      {/* Pulsing radial background glow */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-orange-900/10 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,102,0,0.2)_0%,transparent_70%)] mix-blend-screen animate-[pulse_10s_ease-in-out_infinite]" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 space-y-24">
        
        {/* Section Header */}
        <motion.h2
          className="text-center text-4xl md:text-5xl font-bold text-orange-500"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          About Trinity
        </motion.h2>

        {/* WHAT is Trinity */}
        <motion.div
          className="flex flex-col md:flex-row items-center gap-10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
        >
          {/* Icon with glow */}
          <motion.div
            className="md:w-1/3 relative flex justify-center"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="absolute inset-0 blur-2xl bg-orange-500/10 rounded-full animate-pulse" />
            <Compass className="w-16 h-16 md:w-20 md:h-20 text-orange-500 z-10" />
          </motion.div>

          {/* Text Block */}
          <div className="md:w-2/3 space-y-4">
            <h3 className="text-3xl font-bold text-orange-400">What is Trinity?</h3>
            {/* 23 Nov 2025 Copy */}
            <p className="text-gray-300 text-xl leading-relaxed font-light">
              Trinity is part of the Eternity Solutions <span className="font-bold text-orange-600">AIverse</span>. 
              It is an AI-powered companion built to help tech-led founders, CEOs, and professionals operate with 
              <span className="font-bold text-orange-600"> clarity</span>, <span className="font-bold text-orange-600">purpose</span>, 
              and a daily <span className="font-bold text-orange-600">performance rhythm</span>.
            </p>

            <p className="text-gray-300 text-xl leading-relaxed font-light">
              It captures lived experiences, turns them into structured thinking, strengthens leadership brands, 
              and helps people move from scattered action to <span className="font-bold text-orange-600">coherent progress</span>.
            </p>

            <p className="text-gray-300 text-xl leading-relaxed font-light">
              Trinity brings intelligence, structure, and momentum into the way leaders think, decide, and grow.
            </p>

            {/* <p className="text-gray-300 text-xl leading-relaxed font-light">
              Trinity is part of the Eternity Solutions <span className="font-bold text-orange-600">AIverse</span>. An AI-powered growth companion designed to help tech-led founders and professionals unlock <span className="font-bold text-orange-600 animate-pulse">performance, purpose, and profit</span>. It turns lived experiences into structured thinking, differentiates leadership brands, and drives action with clarity.
            </p> */}
          </div>
        </motion.div>

        {/* WHO is it for */}
        <motion.div
          className="flex flex-col md:flex-row-reverse items-center gap-10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          viewport={{ once: true }}
        >
          {/* Icon with glow */}
          <motion.div
            className="md:w-1/3 relative flex justify-center"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="absolute inset-0 blur-2xl bg-orange-500/10 rounded-full animate-pulse" />
            <Users className="w-16 h-16 md:w-20 md:h-20 text-orange-500 z-10" />
          </motion.div>

          {/* Text Block */}
          <div className="md:w-2/3 space-y-4">
            
            <h3 className="text-3xl font-bold text-orange-400">Who is it for?</h3>
            <div className="space-y-4 text-gray-300 text-xl font-light">
                <div className="flex items-start gap-3">
                <Rocket className="w-6 h-6 mt-1 text-orange-500" />
                {/* <p>
                  Tech-led <span className="font-bold text-orange-600">CEOs & Founders</span> who want to scale their business with clarity and stronger client trust.
                </p> */}
                <p>
                    Tech-led <span className="font-bold text-orange-600">CEOs & Founders</span> aspiring to scale business
                </p>
                </div>
                <div className="flex items-start gap-3">
                <Users className="w-6 h-6 mt-1 text-orange-500" />
                {/* <p>
                  <span className="font-bold text-orange-600">Sales & Account Leaders</span> seeking to deepen relationships, improve conversions, 
                  and elevate influence in the market.
                </p> */}
                <p>
                    <span className="font-bold text-orange-600">Sales & Account leaders</span> seeking better conversion & trust
                </p>
                </div>
                <div className="flex items-start gap-3">
                <Compass className="w-6 h-6 mt-1 text-orange-500" />
                {/* <p>
                  <span className="font-bold text-orange-600">Tech professionals</span> looking to amplify their leadership brand, 
                  convert experience into impact, and accelerate their growth story.
                </p> */}
                <p>
                    <span className="font-bold text-orange-600">Tech professionals</span> aiming to amplify their brand & impact
                </p>
                </div>
            </div>

          </div>
        </motion.div>

        {/* WHY – The Mission */}
        <motion.div
          className="flex flex-col md:flex-row items-center gap-10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          viewport={{ once: true }}
        >
          {/* Icon with glow */}
          <motion.div
            className="md:w-1/3 relative flex justify-center"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="absolute inset-0 blur-2xl bg-orange-500/10 rounded-full animate-pulse" />
            <Rocket className="w-16 h-16 md:w-20 md:h-20 text-orange-500 z-10" />
          </motion.div>

          {/* Text Block */}
          <div className="md:w-2/3 space-y-4">
            <h3 className="text-3xl font-bold text-orange-400">The Mission</h3>
            {/* 23 Nov 2025 Copy */}
            <p className="text-gray-300 text-xl leading-relaxed font-light">
              Trinity’s mission is to be the <span className="font-bold text-orange-600">growth supercharger </span> 
              for tech-led leaders. Helping them multiply their leadership presence, client trust, 
              and daily performance.
            </p>

            <p className="text-gray-300 text-xl leading-relaxed font-light">
              It strengthens both <span className="font-bold text-orange-600">Think</span> and 
              <span className="font-bold text-orange-600"> Run</span> performance — giving leaders a clear view of what matters, 
              and a rhythm that helps them act with purpose every day.
            </p>

            <p className="text-gray-300 text-xl leading-relaxed font-light">
              Every reflection, habit, contribution, and decision becomes an asset that drives 
              <span className="font-bold text-orange-600 animate-pulse"> meaningful growth</span>.
            </p>

            {/* <p className="text-gray-300 text-xl leading-relaxed font-light">
              Trinity’s mission is to be the <span className="font-bold text-orange-600">growth supercharger </span>for Tech Led CxOs. Multiplying their leadership and client impact by enhancing both <strong className="text-orange-400">“Think”</strong> and <strong className="text-orange-400">“Run”</strong> performance. Every habit, reflection, and decision becomes an asset that drives <span className="font-bold text-orange-600 animate-pulse">purposeful</span> momentum.
            </p> */}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
